import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { SubAssembly } from "../../../../types/subAssembly";
import { useApiWithHandling } from "../../../../hooks/useApiWithHandling";
import { useSnackbar } from "../../../../contexts/snackbar-context";
import { useTenantManagementService } from "../../../../services/tenantManagementService";
import {
  MiniPackGenerationDocumentRequest,
  MiniPackGenerationRequest,
  useMiniPackGenerationService,
} from "../../../../services/miniPackGenerationService";
import { jobMatchesSubAssemblyScope } from "../../../../utils/miniPackExportJobUtils";
import { useMiniPackReportService } from "../../../../services/miniPackReportService";
import { isHtmlDocument } from "../../../../utils/htmlDocumentUtils";

type SearchDocument = {
  id: string;
  title?: string;
  fileName?: string;
};

type SearchFolder = {
  name: string;
  documents: SearchDocument[];
  children: SearchFolder[];
};

type SearchDocumentsResponse = {
  data?: {
    folders?: SearchFolder[];
  };
};

type DocumentOption = {
  documentId: string;
  fileName: string;
  displayName: string;
  folderPath: string;
};

type FolderTreeNode = {
  key: string;
  name: string;
  path: string;
  documents: DocumentOption[];
  children: FolderTreeNode[];
};

type SectionState = {
  templateSectionId: string;
  sectionKey: string;
  sectionName: string;
  documents: MiniPackGenerationDocumentRequest[];
};

type Props = {
  open: boolean;
  subAssembly: SubAssembly | null;
  onClose: () => void;
  onOpenJobs: (subAssembly: SubAssembly) => void;
};

const SUPPORTED_MINI_PACK_EXTENSIONS = new Set([".pdf", ".png", ".jpg", ".jpeg", ".txt"]);

const isSupportedForMiniPack = (fileName: string): boolean => {
  const extension = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
  return SUPPORTED_MINI_PACK_EXTENSIONS.has(extension);
};

const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
  panelBg: "#1a1a1a",
  surfaceBg: "#111",
  border: "#333",
  mutedText: "rgba(255,255,255,0.72)",
};

const searchFieldSx = {
  mb: 1,
  "& .MuiInputLabel-root": {
    color: COLORS.mutedText,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: COLORS.primary,
  },
  "& .MuiOutlinedInput-root": {
    color: COLORS.white,
    backgroundColor: COLORS.surfaceBg,
    "& fieldset": {
      borderColor: "#666",
    },
    "&:hover fieldset": {
      borderColor: "#999",
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.primary,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.mutedText,
    opacity: 1,
  },
  "& .MuiSvgIcon-root": {
    color: COLORS.white,
  },
};

const documentTreeContainerSx = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto" as const,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 1,
  backgroundColor: COLORS.surfaceBg,
};

const documentTreeRowSx = (depth: number) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  px: 1,
  py: 0.5,
  borderBottom: `1px solid ${COLORS.border}`,
  pl: 1 + depth * 2,
  "&:hover": {
    backgroundColor: COLORS.panelBg,
  },
});

const treeCheckboxSx = {
  color: "rgba(255,255,255,0.88)",
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
  "&.Mui-checked": {
    color: COLORS.primary,
  },
  "&.MuiCheckbox-indeterminate": {
    color: COLORS.primary,
  },
  "&.Mui-disabled": {
    color: "rgba(255,255,255,0.35)",
  },
};

const SUB_ASSEMBLY_PREFIXES: Record<string, string> = {
  "SHOCK STRUT": "ShockStrut",
  "SIDE STRUT": "SideStrut",
  "2ND SIDE STRUT": "SecondSideStrut",
  "DRAG BRACE": "DragBrace",
  "AUX DRAG": "AuxDrag",
};

const normalizeSerial = (value: string): string => {
  const normalized = String(value || "").trim().toUpperCase();
  if (!normalized) {
    return "";
  }

  return normalized.startsWith("SN") ? normalized : `SN${normalized}`;
};

const toPascalCase = (value: string): string =>
  String(value || "")
    .split(/[\s\-_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");

const buildSubAssemblyFolderName = (subAssemblyType: string, serialNumber: string): string => {
  const key = String(subAssemblyType || "").trim().toUpperCase();
  const prefix = SUB_ASSEMBLY_PREFIXES[key] || toPascalCase(subAssemblyType);
  return `${prefix}.${normalizeSerial(serialNumber)}`;
};

const buildFolderTree = (
  folders: SearchFolder[],
  parentPath = "",
  parentKey = "root",
  seenDocumentIds = new Set<string>()
): FolderTreeNode[] => {
  return folders.map((folder, index) => {
    const folderName = (folder.name || `Folder ${index + 1}`).trim();
    const path = parentPath ? `${parentPath} / ${folderName}` : folderName;
    const key = `${parentKey}/${folderName}-${index}`;

    const documents = (folder.documents || []).reduce<DocumentOption[]>((acc, doc) => {
      const id = (doc.id || "").trim();
      if (!id || seenDocumentIds.has(id)) {
        return acc;
      }

      const fileName = (doc.fileName || doc.title || "Untitled").trim();
      if (isHtmlDocument(fileName)) {
        return acc;
      }

      seenDocumentIds.add(id);
      acc.push({
        documentId: id,
        fileName,
        displayName: (doc.title || fileName || id).trim(),
        folderPath: path,
      });
      return acc;
    }, []);

    return {
      key,
      name: folderName,
      path,
      documents,
      children: buildFolderTree(folder.children || [], path, key, seenDocumentIds),
    };
  });
};

const flattenTreeDocuments = (nodes: FolderTreeNode[]): DocumentOption[] => {
  const output: DocumentOption[] = [];
  nodes.forEach((node) => {
    output.push(...node.documents);
    if (node.children.length > 0) {
      output.push(...flattenTreeDocuments(node.children));
    }
  });
  return output;
};

const filterFolderTree = (nodes: FolderTreeNode[], term: string): FolderTreeNode[] => {
  if (!term) {
    return nodes;
  }

  return nodes.reduce<FolderTreeNode[]>((acc, node) => {
    const folderMatches = node.name.toLowerCase().includes(term) || node.path.toLowerCase().includes(term);
    if (folderMatches) {
      acc.push(node);
      return acc;
    }

    const filteredDocuments = node.documents.filter((doc) => {
      return doc.fileName.toLowerCase().includes(term)
        || doc.displayName.toLowerCase().includes(term)
        || doc.folderPath.toLowerCase().includes(term);
    });

    const filteredChildren = filterFolderTree(node.children, term);

    if (filteredDocuments.length > 0 || filteredChildren.length > 0) {
      acc.push({
        ...node,
        documents: filteredDocuments,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

const collectFolderKeys = (nodes: FolderTreeNode[]): string[] => {
  const output: string[] = [];
  nodes.forEach((node) => {
    output.push(node.key);
    if (node.children.length > 0) {
      output.push(...collectFolderKeys(node.children));
    }
  });
  return output;
};

const collectSelectableDocumentIds = (node: FolderTreeNode): string[] => {
  const ids = node.documents
    .filter((doc) => isSupportedForMiniPack(doc.fileName))
    .map((doc) => doc.documentId);
  node.children.forEach((child) => {
    ids.push(...collectSelectableDocumentIds(child));
  });
  return ids;
};

export const MiniPackSubAssemblyDrawer: React.FC<Props> = ({ open, subAssembly, onClose, onOpenJobs }) => {
  const { post } = useApiWithHandling();
  const { showError, showSuccess } = useSnackbar();
  const { createJob, listJobs, downloadJob } = useMiniPackGenerationService();
  const { getConfiguration, saveConfiguration } = useMiniPackReportService();
  const { getMiniPackTemplates, getMiniPackTemplateSections } = useTenantManagementService();

  const requiredTemplateType = "SubAssembly" as const;
  const subAssemblyId = String(subAssembly?.id || "").trim();
  const serialNumber = String(subAssembly?.serialNumber || "").trim();
  const subAssemblyType = String(subAssembly?.subAssemblyType || "").trim();
  const landingGearType = String(subAssembly?.landingGearType || "").trim();
  const assemblyFolder = useMemo(() => buildSubAssemblyFolderName(subAssemblyType, serialNumber), [subAssemblyType, serialNumber]);

  const [sections, setSections] = useState<SectionState[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [checkingTemplate, setCheckingTemplate] = useState(false);
  const [loadingTemplateSections, setLoadingTemplateSections] = useState(false);
  const [templateAvailable, setTemplateAvailable] = useState(false);
  const [templateFileName, setTemplateFileName] = useState<string>("");
  const [creatingJob, setCreatingJob] = useState(false);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(0);
  const [pickerFilter, setPickerFilter] = useState("");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<string>>(new Set());
  const [expandedFolderKeys, setExpandedFolderKeys] = useState<Set<string>>(new Set());
  const [savingConfiguration, setSavingConfiguration] = useState(false);
  const [hasPreviousMiniPack, setHasPreviousMiniPack] = useState(false);
  const [latestJob, setLatestJob] = useState<any>(null);

  const buildConfigurationPayload = useCallback((nextSections: SectionState[]) => ({
    sourceScope: "subAssembly" as const,
    subAssemblyId,
    sections: nextSections.map((section) => ({
      templateSectionId: section.templateSectionId,
      sectionKey: section.sectionKey,
      sectionName: section.sectionName,
      sortOrder: 0,
      documents: section.documents,
    })),
  }), [subAssemblyId]);

  const persistConfiguration = useCallback(async (nextSections: SectionState[]) => {
    if (!subAssemblyId) {
      return;
    }

    await saveConfiguration(buildConfigurationPayload(nextSections));
  }, [buildConfigurationPayload, saveConfiguration, subAssemblyId]);

  useEffect(() => {
    if (!open) {
      setSections([]);
      setFolderTree([]);
      setExpandedFolderKeys(new Set());
      setTemplateAvailable(false);
      setTemplateFileName("");
      setCheckingTemplate(false);
      setLoadingTemplateSections(false);
      setLoadingDocuments(false);
      setActiveSectionIndex(0);
      setPickerFilter("");
      setSelectedDocumentIds(new Set());
      return;
    }

    if (!subAssembly) {
      return;
    }

    setActiveSectionIndex(0);
    setPickerFilter("");
    setSelectedDocumentIds(new Set());
  }, [open, subAssembly]);

  useEffect(() => {
    if (!open || !subAssembly || !serialNumber) {
      setHasPreviousMiniPack(false);
      return undefined;
    }

    let cancelled = false;

    void listJobs(200)
      .then((items) => {
        if (cancelled) {
          return;
        }

        const matchingJob = items.find((job) => jobMatchesSubAssemblyScope(job, assemblyFolder));
        setHasPreviousMiniPack(!!matchingJob);
        setLatestJob(matchingJob || null);
      })
      .catch(() => {
        if (!cancelled) {
          setHasPreviousMiniPack(false);
          setLatestJob(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [assemblyFolder, open, subAssembly, listJobs, serialNumber]);

  useEffect(() => {
    if (!open || !subAssembly) {
      return;
    }

    let cancelled = false;

    const initializeDrawer = async () => {
      setCheckingTemplate(true);
      setLoadingTemplateSections(false);
      setTemplateAvailable(false);
      setTemplateFileName("");
      setSections([]);

      try {
        const templates = await getMiniPackTemplates();
        if (cancelled) {
          return;
        }

        const matchedTemplate = templates.find((item) => item.templateType === requiredTemplateType);
        if (!matchedTemplate) {
          setTemplateAvailable(false);
          setTemplateFileName("");
          return;
        }

        setTemplateAvailable(true);
        setTemplateFileName(matchedTemplate.fileName || "");
        setLoadingTemplateSections(true);

        const response = await getMiniPackTemplateSections(requiredTemplateType);
        if (cancelled) {
          return;
        }

        const templateSections = (response.sections || [])
          .slice()
          .sort((left, right) => left.sortOrder - right.sortOrder)
          .map((section, index) => ({
            templateSectionId: section.id || "",
            sectionKey: section.sectionKey || `section-${index + 1}`,
            sectionName: section.sectionName || `Section ${index + 1}`,
            documents: [] as MiniPackGenerationDocumentRequest[],
          }));

        let resolvedSections = templateSections;
        if (subAssemblyId) {
          try {
            const savedConfiguration = await getConfiguration({
              sourceScope: "subAssembly",
              subAssemblyId,
            });
            if (savedConfiguration?.hasSavedConfiguration) {
              const documentsBySectionId = new Map(
                (savedConfiguration.sections || []).map((section) => [
                  section.templateSectionId.toLowerCase(),
                  section.documents || [],
                ])
              );

              resolvedSections = templateSections.map((section) => ({
                ...section,
                documents: documentsBySectionId.get(section.templateSectionId.toLowerCase()) ?? [],
              }));
            }
          } catch {
            resolvedSections = templateSections;
          }
        }

        setSections(resolvedSections);
        setActiveSectionIndex(0);
        setSelectedDocumentIds(new Set(
          resolvedSections[0]?.documents.map((document) => document.documentId) ?? []
        ));
      } catch {
        if (!cancelled) {
          setTemplateAvailable(false);
          setTemplateFileName("");
          setSections([]);
        }
      } finally {
        if (!cancelled) {
          setCheckingTemplate(false);
          setLoadingTemplateSections(false);
        }
      }
    };

    void initializeDrawer();

    return () => {
      cancelled = true;
    };
  }, [open, subAssembly, subAssemblyId, requiredTemplateType, getConfiguration, getMiniPackTemplates, getMiniPackTemplateSections]);

  const isInitializing = checkingTemplate || loadingTemplateSections;
  const showWorkspace = Boolean(checkingTemplate || templateAvailable);
  const showPlaceholder = Boolean(!isInitializing && !templateAvailable);
  const canLoadDocuments = Boolean(open && subAssembly && templateAvailable && !isInitializing && landingGearType && assemblyFolder);

  const loadDocuments = useCallback(async () => {
    if (!open || !subAssembly || !canLoadDocuments) {
      return;
    }

    setLoadingDocuments(true);
    try {
      const requestPayload: Record<string, any> = {
        page: 1,
        pageSize: 1000,
        filters: {
          documentScope: "subAssembly",
          assemblyType: "landingGear",
          assemblyFolder,
        },
      };

      const response = await post<SearchDocumentsResponse>("api/documents/search", requestPayload, {
        showLoader: false,
        showErrorSnackbar: false,
        throwOnError: true,
      });

      const folders = response?.data?.data?.folders || [];
      const tree = buildFolderTree(folders);
      setFolderTree(tree);
      setExpandedFolderKeys(new Set(collectFolderKeys(tree)));
    } catch (error) {
      showError((error as Error).message || "Failed to load source documents.");
      setFolderTree([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [open, subAssembly, canLoadDocuments, assemblyFolder, post, showError]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const allDocuments = useMemo(() => flattenTreeDocuments(folderTree), [folderTree]);
  const hasAnyDocuments = allDocuments.length > 0;

  const filteredTree = useMemo(() => {
    const term = pickerFilter.trim().toLowerCase();
    return filterFolderTree(folderTree, term);
  }, [folderTree, pickerFilter]);

  const canGenerate = templateAvailable && sections.some((section) => section.documents.length > 0);
  const activeSection = sections[activeSectionIndex];
  const documentsById = useMemo(() => {
    const map = new Map<string, DocumentOption>();
    allDocuments.forEach((doc) => {
      map.set(doc.documentId, doc);
    });
    return map;
  }, [allDocuments]);

  const selectedDocsForPanel = useMemo(() => {
    const fallbackById = new Map<string, MiniPackGenerationDocumentRequest>();
    if (activeSection) {
      activeSection.documents.forEach((doc) => {
        fallbackById.set(doc.documentId, doc);
      });
    }

    return Array.from(selectedDocumentIds)
      .map((id) => {
        const found = documentsById.get(id);
        if (found) {
          if (!isSupportedForMiniPack(found.fileName)) {
            return null;
          }

          return {
            documentId: found.documentId,
            fileName: found.fileName,
            displayName: found.displayName,
            folderPath: found.folderPath,
          };
        }

        const fallback = fallbackById.get(id);
        if (!fallback) {
          return null;
        }

        if (!isSupportedForMiniPack(fallback.fileName)) {
          return null;
        }

        return {
          documentId: fallback.documentId,
          fileName: fallback.fileName,
          displayName: fallback.displayName,
          folderPath: "",
        };
      })
      .filter((doc): doc is { documentId: string; fileName: string; displayName: string; folderPath: string } => Boolean(doc));
  }, [activeSection, selectedDocumentIds, documentsById]);

  const handleSectionChange = (index: number) => {
    if (!templateAvailable) {
      return;
    }

    const section = sections[index];
    setActiveSectionIndex(index);
    setPickerFilter("");
    setSelectedDocumentIds(
      new Set(
        section.documents
          .filter((doc) => isSupportedForMiniPack(doc.fileName))
          .map((doc) => doc.documentId)
      )
    );
  };

  const applySelectionToSection = async () => {
    if (!activeSection) {
      return;
    }

    const selected = selectedDocsForPanel.map((doc) => ({
      documentId: doc.documentId,
      fileName: doc.fileName,
      displayName: doc.displayName,
    }));

    const nextSections = [...sections];
    nextSections[activeSectionIndex] = {
      ...nextSections[activeSectionIndex],
      documents: selected,
    };

    setSections(nextSections);
    setSavingConfiguration(true);
    try {
      await persistConfiguration(nextSections);
      showSuccess(`${selected.length} document(s) attached to ${activeSection.sectionName}.`);
    } catch (error) {
      showError((error as Error).message || "Failed to save section attachments.");
    } finally {
      setSavingConfiguration(false);
    }
  };

  const handleDownloadLatestJob = async () => {
    if (!latestJob) return;

    try {
      const started = await downloadJob(latestJob);
      if (started) {
        showSuccess("Download started. Check your browser downloads.");
      } else {
        showError("Failed to start mini-pack download.");
      }
    } catch (error) {
      showError((error as Error).message || "Failed to download mini-pack.");
    }
  };

  const handleGenerate = async () => {
    if (!subAssembly || !canGenerate) {
      return;
    }

    const request: MiniPackGenerationRequest = {
      sourceScope: "subAssembly",
      assemblyType: "SubAssembly",
      assemblyFolder,
      subAssemblyId,
      sections: sections.map((section) => ({
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
        documents: section.documents,
      })),
    };

    setCreatingJob(true);
    try {
      await persistConfiguration(sections);
      await createJob(request);
      showSuccess("Mini-pack generation queued.");
      onOpenJobs(subAssembly);
      onClose();
    } catch (error) {
      showError((error as Error).message || "Failed to queue mini-pack generation.");
    } finally {
      setCreatingJob(false);
    }
  };

  const toggleFolderExpanded = (folderKey: string) => {
    setExpandedFolderKeys((previous) => {
      const next = new Set(previous);
      if (next.has(folderKey)) {
        next.delete(folderKey);
      } else {
        next.add(folderKey);
      }
      return next;
    });
  };

  const toggleDocumentSelection = (documentId: string, checked: boolean) => {
    setSelectedDocumentIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        const doc = documentsById.get(documentId);
        if (!doc || !isSupportedForMiniPack(doc.fileName)) {
          return previous;
        }

        next.add(documentId);
      } else {
        next.delete(documentId);
      }
      return next;
    });
  };

  const toggleFolderSelection = (node: FolderTreeNode, checked: boolean) => {
    const documentIds = collectSelectableDocumentIds(node);
    setSelectedDocumentIds((previous) => {
      const next = new Set(previous);
      documentIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const renderFolderNode = (node: FolderTreeNode, depth = 0): React.ReactNode => {
    const isExpanded = expandedFolderKeys.has(node.key);
    const hasChildren = node.children.length > 0;
    const childDocumentIds = collectSelectableDocumentIds(node);
    const selectedCount = childDocumentIds.reduce(
      (count, id) => (selectedDocumentIds.has(id) ? count + 1 : count),
      0
    );
    const hasAnyDocuments = childDocumentIds.length > 0;
    const checked = hasAnyDocuments && selectedCount === childDocumentIds.length;
    const indeterminate = selectedCount > 0 && selectedCount < childDocumentIds.length;

    return (
      <React.Fragment key={node.key}>
        <Box sx={documentTreeRowSx(depth)}>
          {hasChildren ? (
            <IconButton size="small" onClick={() => toggleFolderExpanded(node.key)} sx={{ color: COLORS.white }}>
              {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32 }} />
          )}
          <Checkbox
            size="small"
            checked={checked}
            indeterminate={indeterminate}
            disabled={!hasAnyDocuments}
            onChange={(event) => toggleFolderSelection(node, event.target.checked)}
            sx={treeCheckboxSx}
          />
          <FolderIcon fontSize="small" sx={{ color: COLORS.primary }} />
          <Box
            onClick={() => toggleFolderExpanded(node.key)}
            sx={{
              color: COLORS.white,
              flex: 1,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <Typography sx={{ color: COLORS.white }}>{node.name}</Typography>
          </Box>
        </Box>

        {isExpanded && node.documents.map((doc) => {
          const isSupported = isSupportedForMiniPack(doc.fileName);
          return (
            <Box key={doc.documentId} sx={documentTreeRowSx(depth + 1)}>
              <Box sx={{ width: 32 }} />
              <Checkbox
                size="small"
                checked={isSupported && selectedDocumentIds.has(doc.documentId)}
                disabled={!isSupported}
                onChange={(event) => toggleDocumentSelection(doc.documentId, event.target.checked)}
                sx={treeCheckboxSx}
              />
              <DescriptionIcon fontSize="small" sx={{ color: COLORS.white }} />
              <Box sx={{ flex: 1, minWidth: 0, opacity: isSupported ? 1 : 0.6 }}>
                <Typography sx={{ color: COLORS.white }} noWrap>
                  {doc.displayName}
                </Typography>
                <Typography variant="caption" sx={{ color: COLORS.mutedText }} noWrap>
                  {doc.folderPath}
                </Typography>
              </Box>
            </Box>
          );
        })}

        {isExpanded && node.children.map((child) => renderFolderNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!activeSection) {
      return;
    }

    const nextSections = [...sections];
    nextSections[activeSectionIndex] = {
      ...nextSections[activeSectionIndex],
      documents: nextSections[activeSectionIndex].documents.filter((doc) => doc.documentId !== documentId),
    };

    setSections(nextSections);
    setSelectedDocumentIds((previous) => {
      const next = new Set(previous);
      next.delete(documentId);
      return next;
    });

    setSavingConfiguration(true);
    try {
      await persistConfiguration(nextSections);
    } catch (error) {
      showError((error as Error).message || "Failed to update section attachments.");
    } finally {
      setSavingConfiguration(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", md: 1100 }, bgcolor: COLORS.panelBg, color: COLORS.white } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.primary, fontWeight: 700 }}>
              Configure Mini-Pack
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.mutedText }}>
              {subAssemblyType || "Sub-Assembly"} / {serialNumber || "Unspecified"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: COLORS.white }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {templateAvailable && templateFileName && (
          <Box sx={{ px: 2, pt: 1.5 }}>
            <Alert severity="success">
              Landing Gear Sub-Assemblies template loaded: {templateFileName}
            </Alert>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, p: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "260px minmax(320px, 1fr) minmax(320px, 1fr)" }, gap: 2 }}>
          <Paper sx={{ p: 1.5, bgcolor: COLORS.surfaceBg, border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: COLORS.white }}>
              Sections
            </Typography>

            {isInitializing ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : showPlaceholder ? (
              <Alert severity="warning">Upload a Landing Gear Sub-Assembly mini-pack template before generating.</Alert>
            ) : (
              <Stack spacing={1} sx={{ overflowY: "auto" }}>
                {sections.map((section, index) => {
                  const isActive = index === activeSectionIndex;
                  return (
                    <Button
                      key={section.templateSectionId || section.sectionKey}
                      variant={isActive ? "contained" : "outlined"}
                      onClick={() => handleSectionChange(index)}
                      sx={{
                        justifyContent: "space-between",
                        textTransform: "none",
                        bgcolor: isActive ? COLORS.primary : "transparent",
                        borderColor: COLORS.border,
                        color: COLORS.white,
                      }}
                    >
                      <span>{section.sectionName}</span>
                      <span>{section.documents.length}</span>
                    </Button>
                  );
                })}
              </Stack>
            )}
          </Paper>

          <Paper sx={{ p: 1.5, bgcolor: COLORS.surfaceBg, border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.white }}>
                Document Browser
              </Typography>
            </Stack>

            <TextField
              size="small"
              placeholder="Search by file name, title, or folder path"
              value={pickerFilter}
              onChange={(event) => setPickerFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={searchFieldSx}
            />

            {!showWorkspace ? null : loadingDocuments ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : !hasAnyDocuments ? (
              <Alert severity="info">No supported documents found for this sub-assembly folder.</Alert>
            ) : (
              <Box sx={documentTreeContainerSx}>
                {filteredTree.map((node) => renderFolderNode(node))}
              </Box>
            )}

          </Paper>

          <Paper sx={{ p: 1.5, bgcolor: COLORS.surfaceBg, border: `1px solid ${COLORS.border}`, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.white }}>
                Selected Docs
              </Typography>
              {hasPreviousMiniPack && latestJob && (
                <Button size="small" onClick={handleDownloadLatestJob} sx={{ color: COLORS.primary }}>
                  Download Latest
                </Button>
              )}
            </Stack>

            <Typography variant="body2" sx={{ color: COLORS.mutedText, mb: 1 }}>
              For section: {activeSection?.sectionName || "None"}
            </Typography>

            <Box sx={{ ...documentTreeContainerSx, p: 0 }}>
              {selectedDocsForPanel.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ color: COLORS.mutedText }}>No documents selected.</Typography>
                </Box>
              ) : (
                selectedDocsForPanel.map((doc) => (
                  <Box key={doc.documentId} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 1, borderBottom: `1px solid ${COLORS.border}` }}>
                    <DescriptionIcon fontSize="small" sx={{ color: COLORS.white }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: COLORS.white }} noWrap>
                        {doc.displayName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.mutedText }} noWrap>
                        {doc.folderPath || doc.fileName}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => void handleRemoveDocument(doc.documentId)} sx={{ color: COLORS.white }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                onClick={() => setSelectedDocumentIds(new Set())}
                sx={{
                  textTransform: "none",
                  color: COLORS.white,
                  borderColor: COLORS.border,
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                size="small"
                fullWidth
                onClick={() => {
                  void applySelectionToSection();
                }}
                disabled={!activeSection || savingConfiguration}
                sx={{
                  textTransform: "none",
                  bgcolor: COLORS.primary,
                  "&:hover": { bgcolor: COLORS.primaryHover },
                }}
              >
                {savingConfiguration ? "Saving..." : "Attach"}
              </Button>
            </Stack>

          </Paper>
        </Box>

        <Divider sx={{ mx: 2, my: 1.5, borderColor: COLORS.border }} />

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ px: 2, pb: 2 }}>
          <Button
            variant="text"
            onClick={() => subAssembly && onOpenJobs(subAssembly)}
            disabled={!subAssembly}
            sx={{ textTransform: "none", color: "#bbb" }}
          >
            View Jobs
          </Button>

          <Button
            variant="contained"
            startIcon={creatingJob ? <CircularProgress size={16} sx={{ color: COLORS.white }} /> : <SaveIcon />}
            onClick={handleGenerate}
            disabled={creatingJob || savingConfiguration || isInitializing || !canGenerate}
            sx={{
              textTransform: "none",
              bgcolor: COLORS.primary,
              "&:hover": { bgcolor: COLORS.primaryHover },
            }}
          >
            {creatingJob ? "Queuing..." : "Save & Generate PDF"}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};